import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import logo from './logo.avif';

function Home() {
  const [ctcData, setCtcData] = useState([]);
  const navigate = useNavigate();
  const [importError, setImportError] = useState('');

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('ctcData')) || [];
    setCtcData(storedData);
  }, []);

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const updatedData = ctcData.filter((item) => item.id !== id);
      setCtcData(updatedData);
      localStorage.setItem('ctcData', JSON.stringify(updatedData));
    }
  };

  const handleEdit = (item) => {
    navigate('/ctc', { state: { item } });
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return value;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(Math.round(value));
  };

  const calculateBreakup = (item) => {
    const ctcValue = parseFloat(item.ctc);
    const miValue = parseFloat(item.medicalInsurance);
    let vpValue = parseFloat(item.variablePay) || 0;
    let dpValue = parseFloat(item.deferredPay) || 0;

    let calcaEpf;
    if (ctcValue > 353360) {
      calcaEpf = 21600;
    } else {
      calcaEpf = ctcValue * 0.0603821428571428;
    }

    const actualValue = (ctcValue - (vpValue + dpValue + miValue + calcaEpf + calcaEpf)) / 1.028845;

    const abasicSalary = actualValue * 0.5;
    const mbasicSalary = abasicSalary / 12;

    const ahraAmount = actualValue * 0.3;
    const mhraAmount = ahraAmount / 12;

    const adaAmount = actualValue * 0.1;
    const mdaAmount = adaAmount / 12;

    const aconAmount = actualValue * 0.04;
    const mconAmount = aconAmount / 12;

    const amaAmount = actualValue * 0.03;
    const mmaAmount = amaAmount / 12;

    const asaAmount = actualValue * 0.03;
    const msaAmount = asaAmount / 12;

    const astAmount = abasicSalary + ahraAmount + adaAmount + aconAmount + amaAmount + asaAmount + calcaEpf;
    const mstAmount = mbasicSalary + mhraAmount + mdaAmount + mconAmount + mmaAmount + msaAmount + calcaEpf / 12;

    const graAmount = (mbasicSalary + mdaAmount) * 0.5769;

    const asubtoAmount = calcaEpf + miValue + graAmount + vpValue + dpValue;

    const agsAmount = astAmount + asubtoAmount;

    return {
      ...item,
      annualBasicSalary: abasicSalary,
      annualHra: ahraAmount,
      annualDa: adaAmount,
      annualCon: aconAmount,
      annualMedicalAllowance: amaAmount,
      annualSpecialAllowance: asaAmount,
      annualEpf: calcaEpf,
      annualGrossSalary1: astAmount,
      annualGrossSalary2: asubtoAmount,
      gratuity: graAmount,
      ctc: agsAmount,
      monthlyBasicSalary: mbasicSalary,
      monthlyHra: mhraAmount,
      monthlyDa: mdaAmount,
      monthlyCon: mconAmount,
      monthlyMedicalAllowance: mmaAmount,
      monthlySpecialAllowance: msaAmount,
      monthlyEpf: calcaEpf / 12,
      monthlyGrossSalary1: mstAmount,
    };
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      const binaryStr = event.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(worksheet);

      for (const row of data) {
        if (typeof row.ctc === 'string' || isNaN(row.ctc)) {
          setImportError('Invalid data in CTC column. It must be a number.');
          return;
        }
        if (typeof row.medicalInsurance === 'string' || isNaN(row.medicalInsurance)) {
          setImportError('Invalid data in Medical Insurance column. It must be a number.');
          return;
        }
        if (typeof row.variablePay === 'string' || isNaN(row.variablePay)) {
          setImportError('Invalid data in Variable Pay column. It must be a number.');
          return;
        }
        if (typeof row.deferredPay === 'string' || isNaN(row.deferredPay)) {
          setImportError('Invalid data in Deferred Pay column. It must be a number.');
          return;
        }
      }

      const calculatedData = data.map((item) => {
        const newItem = { ...item, id: uuidv4() };
        return calculateBreakup(newItem);
      });

      const updatedData = [...ctcData, ...calculatedData];
      setCtcData(updatedData);
      localStorage.setItem('ctcData', JSON.stringify(updatedData));
    };

    reader.readAsBinaryString(file);
  };

  const handleExport = () => {
    const exportData = ctcData.map(item => {
      return {
        'Name': item.name,
        'Designation': item.designation,
        'Basic Salary': Math.round(item.annualBasicSalary),
        'HRA': Math.round(item.annualHra),
        'DA': Math.round(item.annualDa),
        'Coveyance': Math.round(item.annualCon),
        'Medical Allowance': Math.round(item.annualMedicalAllowance),
        'Special Allowance': Math.round(item.annualSpecialAllowance),
        'EPF Employee': Math.round(item.annualEpf),
        'Gross Salary (1)': Math.round(item.annualGrossSalary1),
        'EPF Employer': Math.round(item.annualEpf),
        'Medical Insurance': Math.round(item.medicalInsurance),
        'Variable Pay': Math.round(item.variablePay),
        'Deferred Pay': Math.round(item.deferredPay),
        'Gratuity': Math.round(item.gratuity),
        'Gross Salary (2)': Math.round(item.annualGrossSalary2),
        'CTC (1)+(2)': Math.round(item.ctc),
        'Basic Salary ': Math.round(item.monthlyBasicSalary),
        'HRA ': Math.round(item.monthlyHra),
        'DA ': Math.round(item.monthlyDa),
        'Coveyance ': Math.round(item.monthlyCon),
        'Medical Allowance ': Math.round(item.monthlyMedicalAllowance),
        'Special Allowance ': Math.round(item.monthlySpecialAllowance),
        'EPF Employee ': Math.round(item.monthlyEpf),
        'Gross Salary (1) ': Math.round(item.monthlyGrossSalary1)
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'CTC Data');
    XLSX.writeFile(workbook, 'ctc_data.xlsx');
  };

  const handleDownloadSample = () => {
    const headers = [
      'name',
      'designation',
      'ctc',
      'medicalInsurance',
      'variablePay',
      'deferredPay',
    ];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample Data');
    XLSX.writeFile(workbook, 'sample_ctc_data.xlsx');
  };

  const clearTableData = () => {
    if (window.confirm('Are you sure you want to clear all data?')) {
      setCtcData([]);
      localStorage.setItem('ctcData', JSON.stringify([]));
    }
  };

  return (
    <div className="home-page">
      <div className="header-container">
        <div className="logo-container">
          <img className="logo" src={logo} alt="idc Logo"  />
          <h2 className="header-title">CTC Calculator</h2>
        </div>
        <br></br>
        <div className="button-container">
          <button className="add-button" onClick={() => navigate('/ctc')}>
            Add CTC
          </button>
          <button className="add-button" onClick={handleDownloadSample}>Download Sample</button>
          <input type="file" className="add-button" onChange={handleImport} accept=".xlsx, .xls" />
          <button className="add-button" onClick={handleExport}>Export</button>
          <button className="add-button" onClick={clearTableData}>Clear Data</button>
          
        </div>
        {importError && <div className="error">{importError}</div>}
      </div>
      
      <table className="table-container">
        <thead>
          <tr>
            <th colSpan={2}>Candidate</th>
            <th colSpan={15}>Per Annum</th>
            <th colSpan={8}>Per Month</th>
            <th>Actions</th>
          </tr>
          <tr>
            <th>Name</th>
            <th>Designation</th>
            <th>Basic Salary</th>
            <th>HRA</th>
            <th>DA</th>
            <th>Coveyance</th>
            <th>Medical Allowance</th>
            <th>Special Allowance</th>
            <th>EPF Employee</th>
            <th>Gross Salary (1)</th>
            <th>EPF Employer</th>
            <th>Medical Insurance</th>
            <th>Variable Pay</th>
            <th>Deferred Pay</th>
            <th>Gratuity</th>
            <th>Gross Salary (2)</th>
            <th>CTC (1)+(2)</th>
            <th>Basic Salary</th>
            <th>HRA</th>
            <th>DA</th>
            <th>Coveyance</th>
            <th>Medical Allowance</th>
            <th>Special Allowance</th>
            <th>EPF Employee</th>
            <th>Gross Salary (1)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {ctcData.length === 0 ? (
            <tr>
              <td colSpan="26">No CTC data available.</td>
            </tr>
          ) : (
            ctcData.map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.designation}</td>
                <td>{formatCurrency(item.annualBasicSalary)}</td>
                <td>{formatCurrency(item.annualHra)}</td>
                <td>{formatCurrency(item.annualDa)}</td>
                <td>{formatCurrency(item.annualCon)}</td>
                <td>{formatCurrency(item.annualMedicalAllowance)}</td>
                <td>{formatCurrency(item.annualSpecialAllowance)}</td>
                <td>{formatCurrency(item.annualEpf)}</td>
                <td>{formatCurrency(item.annualGrossSalary1)}</td>
                <td>{formatCurrency(item.annualEpf)}</td>
                <td>{formatCurrency(item.medicalInsurance)}</td>
                <td>{formatCurrency(item.variablePay)}</td>
                <td>{formatCurrency(item.deferredPay)}</td>
                <td>{formatCurrency(item.gratuity)}</td>
                <td>{formatCurrency(item.annualGrossSalary2)}</td>
                <td>{formatCurrency(item.ctc)}</td>
                <td>{formatCurrency(item.monthlyBasicSalary)}</td>
                <td>{formatCurrency(item.monthlyHra)}</td>
                <td>{formatCurrency(item.monthlyDa)}</td>
                <td>{formatCurrency(item.monthlyCon)}</td>
                <td>{formatCurrency(item.monthlyMedicalAllowance)}</td>
                <td>{formatCurrency(item.monthlySpecialAllowance)}</td>
                <td>{formatCurrency(item.monthlyEpf)}</td>
                <td>{formatCurrency(item.monthlyGrossSalary1)}</td>
                <td>
                  <button className="edit-button" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                  <button className="button_del" onClick={() => handleDelete(item.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <br />
    
      </div>
    
  );
}

export default Home;